import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ApiService from '../../../ApiService';
import { AgencyContext } from '../../../App';
import { Agency } from '../../../types/Agency';
import { Dealer as Advertiser } from '../../../types/Dealer';
import { formatYYYYMMDD, parseYYYYMMDD } from '../HatUtils';
import UrlUtils from '../../../utils/UrlUtils';
import { getValueForMediaFilter, HatMediaType, HatQueryCriteria, tryGetHatMediaType } from '../HatMetrics';

export type HatDbCriteria = {
    mediaFilter: HatMediaType;
    domainFilter: boolean;
    agencyId: number | null;
    apiToken: string;
    advertiserId: number | null;
    currentRangeStart: Dayjs;
    currentRangeEnd: Dayjs;
    previousRangeStart: Dayjs | null;
    previousRangeEnd: Dayjs | null;
    agency?: Agency | null;
    advertiser?: Advertiser | null;
};

type HatClicksteamConfig = {
    baseUrl: string;
    authToken: string;
};

export type HatDbContextValue = {
    clickstreamConfig: HatClicksteamConfig;
    setClickstreamConfig: Dispatch<SetStateAction<HatClicksteamConfig>>;
    hatDbCriteria: HatDbCriteria;
    setHatDbCriteria: Dispatch<SetStateAction<HatDbCriteria>>;
    getCurrentRangeCriteria: () => HatQueryCriteria;
    getPreviousRangeCriteria: () => HatQueryCriteria | null;
    sharedToken: boolean;
    agencyName: string | null;
    advertiserName: string | null;
    advertiserDomain: string | null;
};

export type HatDbContextInitialValues = {
    agencyId: number | null;
    advertiserId: number | null;
    mediaFilter: HatMediaType;
    domainFilter: boolean;
    currentRangeStart: Dayjs;
    currentRangeEnd: Dayjs;
    previousRangeStart: Dayjs | null;
    previousRangeEnd: Dayjs | null;
};

export const HatDbContext = createContext<HatDbContextValue | null>(null);

/**
 * The HAT dashboard context contains the dashboard criteria as well as any additional context required by the
 * dashboard.
 */
export const useHatDbContext = (): HatDbContextValue => {
    const context: HatDbContextValue | null = useContext(HatDbContext);
    if (!context) {
        throw new Error('useHatDbContext must be used within a HatDbContextProvider');
    }

    return context;
};

/**
 * Create an instance of the dashboard context object.
 * @param initialValues Initial values for the context.
 * @returns The dashboard context value.
 */
export function useHatDbContextFactory(initialValues: HatDbContextInitialValues): HatDbContextValue {
    const [hatDbCriteria, setHatDbCriteria] = useState<HatDbCriteria>({
        mediaFilter: initialValues.mediaFilter,
        domainFilter: initialValues.domainFilter,
        agencyId: initialValues.agencyId,
        advertiserId: initialValues.advertiserId,
        apiToken: '',
        currentRangeStart: initialValues.currentRangeStart || dayjs(),
        currentRangeEnd: initialValues.currentRangeEnd || dayjs(),
        previousRangeStart: initialValues.previousRangeStart,
        previousRangeEnd: initialValues.previousRangeEnd,
    });

    const [clickstreamConfig, setClickstreamConfig] = useState<HatClicksteamConfig>({
        baseUrl: '',
        authToken: '',
    });

    // Fetch the HAT API token and a CS auth token whenever the advertiser ID changes.
    const hatConfigQuery = useQuery({
        queryKey: ['hatDbCriteriaAdvertiserId', hatDbCriteria.advertiserId],
        queryFn: async () => {
            if (hatDbCriteria.advertiserId != null) {
                let res = await Promise.all([
                    ApiService.getDealer(hatDbCriteria.advertiserId),
                    ApiService.getHatConfig(hatDbCriteria.advertiserId),
                ]);
                return {
                    // Remove protocol and www. from domain when filtering.
                    agencyName: res[0].data?.agency?.name,
                    advertiserName: res[0].data?.dealerName,
                    advertiserDomain: UrlUtils.strip(res[0].data?.url),
                    apiToken: res[0].data?.tfApiToken,
                    clickstreamUrl: res[1].data?.clickstreamUrl,
                    authToken: res[1].data?.clickstreamXAuthToken,
                    sharedToken: res[1].data?.duplicateClickstreamXAuthToken,
                };
            } else {
                return null;
            }
        },
        staleTime: 1800 * 1000, // 30 minutes
        enabled: !!hatDbCriteria.advertiserId,
    });

    // Update the API token on the context whenever the API token changes.
    useEffect(() => {
        if (hatConfigQuery.data) {
            const { apiToken, clickstreamUrl, authToken } = hatConfigQuery.data;
            setHatDbCriteria((previousValue) => ({
                ...previousValue,
                apiToken,
                // Clear the domain filter flag if the new API token isn't shared.
                domainFilter: hatConfigQuery.data?.sharedToken ? previousValue.domainFilter : false,
            }));
            setClickstreamConfig((previousValue) => ({
                ...previousValue,
                baseUrl: clickstreamUrl,
                authToken,
            }));
        }
    }, [hatConfigQuery.data]);

    const getCurrentRangeCriteria = useCallback(
        (): HatQueryCriteria => ({
            mediaFilter: getValueForMediaFilter(hatDbCriteria.mediaFilter),
            domainFilter: hatDbCriteria.domainFilter ? hatConfigQuery.data?.advertiserDomain : null,
            browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            advertiserId: null, //hatDbCriteria.advertiserId,
            apiToken: hatDbCriteria.apiToken,
            rangeStart: hatDbCriteria.currentRangeStart,
            rangeEnd: hatDbCriteria.currentRangeEnd,
            rangePeriod: 'daily',
        }),
        [
            hatDbCriteria.mediaFilter,
            hatDbCriteria.domainFilter,
            hatDbCriteria.apiToken,
            hatDbCriteria.currentRangeStart,
            hatDbCriteria.currentRangeEnd,
            hatConfigQuery.data?.advertiserDomain,
        ]
    );

    const getPreviousRangeCriteria = useCallback((): HatQueryCriteria | null => {
        if (hatDbCriteria.previousRangeStart != null && hatDbCriteria.previousRangeEnd != null) {
            return {
                mediaFilter: getValueForMediaFilter(hatDbCriteria.mediaFilter),
                domainFilter: hatDbCriteria.domainFilter ? hatConfigQuery.data?.advertiserDomain : null,
                browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                advertiserId: null, //hatDbCriteria.advertiserId,
                apiToken: hatDbCriteria.apiToken,
                rangeStart: hatDbCriteria.previousRangeStart,
                rangeEnd: hatDbCriteria.previousRangeEnd,
                rangePeriod: 'daily',
            };
        }

        return null;
    }, [
        hatDbCriteria.mediaFilter,
        hatDbCriteria.domainFilter,
        hatDbCriteria.apiToken,
        hatDbCriteria.previousRangeStart,
        hatDbCriteria.previousRangeEnd,
        hatConfigQuery.data?.advertiserDomain,
    ]);

    // Memoize this context object to keep it stable.
    return useMemo(
        () => ({
            clickstreamConfig,
            setClickstreamConfig,
            hatDbCriteria,
            setHatDbCriteria,
            getCurrentRangeCriteria,
            getPreviousRangeCriteria,
            sharedToken: hatConfigQuery.data?.sharedToken ?? false,

            // Used in things like exports and are not criteria.
            agencyName: hatConfigQuery.data?.agencyName ?? null,
            advertiserName: hatConfigQuery.data?.advertiserName ?? null,
            advertiserDomain: hatConfigQuery.data?.advertiserDomain ?? null,
        }),
        [
            clickstreamConfig,
            setClickstreamConfig,
            hatDbCriteria,
            setHatDbCriteria,
            getCurrentRangeCriteria,
            getPreviousRangeCriteria,
            hatConfigQuery.data?.sharedToken,

            // Used in things like exports and are not criteria.
            hatConfigQuery.data?.agencyName,
            hatConfigQuery.data?.advertiserName,
            hatConfigQuery.data?.advertiserDomain,
        ]
    );
}

// Allow TypeScript to properly understand a .filter() call that removes nulls.
const isNotNull = <T,>(value: T | null | undefined): value is T => value !== null;

/**
 * @returns Build an initial criteria for the dashboard context using URL parameters, saved values from local storage,
 * or defaults, in that order.
 */
export function useHatDbInitialCriteria() {
    const agency = useContext(AgencyContext);
    const params = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Set the default criteria for a dashboard.
    const currentRangeStart = dayjs().startOf('month'),
        currentRangeEnd = dayjs().endOf('day'),
        previousRangeStart = null,
        previousRangeEnd = null;

    const initialCriteria: HatDbContextInitialValues = {
        advertiserId: null,
        agencyId: agency?.id ?? null,
        mediaFilter: HatMediaType.allMedia,
        domainFilter: false,
        currentRangeStart,
        currentRangeEnd,
        previousRangeStart,
        previousRangeEnd,
    };

    // Syncthronize the selected media type with the route parameter.
    let mediaType: HatMediaType | null = HatMediaType.allMedia;
    if (params.mediaType) {
        let tryMediaType = tryGetHatMediaType(params.mediaType);
        if (!tryMediaType) {
            mediaType = HatMediaType.allMedia;
            navigate(`/hat/${mediaType}`, { replace: true });
        } else {
            mediaType = tryMediaType;
        }
    }

    /**
     * Invokes the handler function with the value returned by the first non-undefined locator function.
     * @param restoreFn Handler to apply the value once it is located.
     * @param locatorFns Functions to locate the value to be restored.
     */
    function restoreValueIfPresent<T>(
        restoreFn: (value: T | null) => void,
        ...locatorFns: (() => T | null | undefined)[]
    ) {
        for (const fn of locatorFns) {
            const value = fn();
            if (value !== undefined) {
                restoreFn(value);
                return;
            }
        }
    }

    /**
     * Locate a value in the URL search parameters.
     * @param key The name of the value to locate.
     * @param transformFn An optional function to transform the parameter.
     */
    function checkSearchParams<T>(key: string, transformFn?: (value: string) => T | null | undefined) {
        return () => {
            const value = searchParams.get(key);
            if (value) {
                if (transformFn) {
                    return transformFn(value);
                } else {
                    // You better be expecting a string.
                    return value as T;
                }
            } else {
                return undefined;
            }
        };
    }

    // Deserialize the stored criteria.
    let storedValues: any = {};
    try {
        storedValues = JSON.parse(localStorage.getItem('hatDbCriteria') ?? '{}');
    } catch (e) {
        // Failed to deserialize criteria.
        localStorage.removeItem('hatDbCriteria');
    }

    /**
     * Locate a value in local storage.
     * @param key The name of the value to locate.
     * @param transformFn An optional function to transform the parameter.
     */
    function checkLocalStorage<T, R>(key: string, transformFn?: (value: T | null) => R | null | undefined) {
        return () => {
            const value = storedValues[key];
            if (value === undefined) {
                return undefined;
            } else {
                if (transformFn) {
                    return transformFn(value as T | null);
                } else {
                    return value as R | null;
                }
            }
        };
    }

    /**
     * Try to parse a value as a number.
     */
    function tryParseNumber(value: string, defaultValue: number | null | undefined = undefined) {
        const intValue = parseInt(value);
        if (Number.isFinite(intValue)) {
            return intValue;
        } else {
            return defaultValue;
        }
    }

    /**
     * Try to parse a date range in the format YYYYMMDD-YYYYMMDD.
     */
    function tryParseDateRange(value: string | null): Dayjs[] | null | undefined {
        if (value) {
            const range = value
                .split('-')
                .map((v) => parseYYYYMMDD(v))
                .filter(isNotNull);

            if (range.length === 2 && range[0].isBefore(range[1])) {
                return range;
            }
        }

        return undefined;
    }

    // Synchronize the other criteria with the search parameters.
    // Agency ID
    restoreValueIfPresent<number>(
        (foundValue) => (initialCriteria.agencyId = foundValue),
        checkSearchParams<number>('ag', tryParseNumber),
        checkLocalStorage<number, number>('agencyId')
    );

    // Advertiser ID
    restoreValueIfPresent<number>(
        (foundValue) => (initialCriteria.advertiserId = foundValue),
        checkSearchParams('ad', tryParseNumber),
        checkLocalStorage<number, number>('advertiserId')
    );

    // Current range
    restoreValueIfPresent<Dayjs[]>(
        (foundValue) => {
            if (foundValue) {
                initialCriteria.currentRangeStart = foundValue[0];
                initialCriteria.currentRangeEnd = foundValue[1];
            }
        },
        checkSearchParams('cr', tryParseDateRange),
        checkLocalStorage<string, Dayjs[]>('currentRange', tryParseDateRange)
    );

    // Previous range
    restoreValueIfPresent<Dayjs[]>(
        (foundValue) => {
            if (foundValue) {
                initialCriteria.previousRangeStart = foundValue[0];
                initialCriteria.previousRangeEnd = foundValue[1];
            }
        },
        checkSearchParams('pr', tryParseDateRange),
        checkLocalStorage<string, Dayjs[]>('previousRange', tryParseDateRange)
    );

    // Domain filter.
    restoreValueIfPresent(
        (foundValue) => (initialCriteria.domainFilter = foundValue || false),
        checkSearchParams('df', (value) => value === '1'),
        checkLocalStorage<boolean, boolean>('domainFilter')
    );

    return initialCriteria;
}

/**
 * Synchronize the dashboard criteria with local storage.
 */
export function useHatDbStoreCriteria(hatDbCriteria: HatDbCriteria) {
    useEffect(() => {
        const {
            agencyId,
            advertiserId,
            currentRangeStart,
            currentRangeEnd,
            previousRangeStart,
            previousRangeEnd,
            domainFilter,
        } = hatDbCriteria;

        const data: any = {
            agencyId,
            advertiserId,
            currentRange: `${formatYYYYMMDD(currentRangeStart)}-${formatYYYYMMDD(currentRangeEnd)}`,
            domainFilter,
        };

        if (previousRangeStart && previousRangeEnd) {
            data.previousRange = `${formatYYYYMMDD(previousRangeStart)}-${formatYYYYMMDD(previousRangeEnd)}`;
        }

        try {
            localStorage.setItem('hatDbCriteria', JSON.stringify(data));
        } catch (e) {
            // Storage is disabled.
        }
    }, [hatDbCriteria]);
}

/**
 * Synchronize the dashboard criteria with the search parameters.
 */
export function useHatDbSyncSearchParams(hatDbCriteria: HatDbCriteria) {
    const [, setSearchParams] = useSearchParams();

    useEffect(() => {
        const {
            agencyId,
            advertiserId,
            currentRangeStart,
            currentRangeEnd,
            previousRangeStart,
            previousRangeEnd,
            domainFilter,
        } = hatDbCriteria;

        let params = [];

        if (agencyId) {
            params.push(`ag=${agencyId}`);
        }

        if (advertiserId) {
            params.push(`ad=${advertiserId}`);
        }

        if (currentRangeStart && currentRangeEnd) {
            params.push(`cr=${formatYYYYMMDD(currentRangeStart)}-${formatYYYYMMDD(currentRangeEnd)}`);
        }

        if (previousRangeStart && previousRangeEnd) {
            params.push(`pr=${formatYYYYMMDD(previousRangeStart)}-${formatYYYYMMDD(previousRangeEnd)}`);
        }

        if (domainFilter) {
            params.push('df=1');
        }

        setSearchParams(params.join('&'));
    }, [hatDbCriteria, setSearchParams]);
}
